import { withObservables } from '@nozbe/watermelondb/react'
import {
  EyeCrossIcon,
  GoogleProblem,
  MsgTitle,
  MsgType,
  MsgWhen,
  MsgWho,
  GroupChannelIcon,
  PinIcon,
  ReminderCrossIcon,
  ShowOnlyMyEventsIcon,
  GroupDefaultIcon
} from 'assets/icons'
import { GroupPrivacy } from 'model/models/Group'
import ObserveService from 'services/db/Observe'
import {
  AccountDoc,
  EventDoc,
  GroupDoc,
  UserSettingsDoc
} from 'types/api/changes'
import { Model } from 'types/model'
import {
  defaultGroupImage,
  formatLastUpdate,
  getGroupMessage,
  isGroupChannel,
  isGroupOutOfTier
} from 'utils/api/groups'

import * as Styled from '../styled'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import GroupTitle from 'components/popups/groupInfo/GroupTitle'
import { highestActiveTier } from 'utils/api/accounts'
import ContainerWithBottomBadgeIcon from 'components/containers/ContainerWithBottomBadgeIcon'
import { useTranslation } from 'react-i18next'

import { PrivateLabel } from '../styled'
import { useState, useEffect, useRef } from 'react'
import { APP_COLOR } from 'utils/colors'
import { GoogleCalendar, ICalendar } from 'types/google_events'
import Database from 'services/db/Database'
import { isGroupArchived, isGroupUnread } from 'utils/api/userSettings'
import { tfmGoogleTempLabel } from 'services/db/Queries/GroupQueries'
import { useCalendar } from 'components/providers/CalendarProvider'
import {
  Box,
  CircularProgress,
  IconButton,
  Grid,
  darken,
  lighten,
  Grid2,
  useTheme
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'

import { groupToProvider, isConvertedGroup, ProviderType } from 'utils/groups'
import { ResyncCalendarDialog } from 'components/dialogs/ResyncGoogleDialog'
import { calendarsGif } from 'assets/gif'

import TitleWithIconRow from './TitleWithIconRow'
import { LastUpdateMessagePerGroup } from 'components/sidebar/Groups'
import { on } from 'events'
import { prefixGoogleEventsGroup } from 'utils/constants'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import { isTrulyEmpty, removePrefix } from 'utils/object'
import { last } from 'lodash'
import GroupcalTooltip from 'components/tooltips/GroupcalTooltip'
import {
  GroupMenuAllCalendars,
  GroupMenuDefault,
  GroupMenuReminders,
  GroupMenuYouOwn
} from 'assets/icons/group-menu'
import { alpha } from '@material-ui/core'

interface GroupCardProps {
  events?: Model<EventDoc>[] | undefined
  account?: Model<AccountDoc>
  userSettings: Model<UserSettingsDoc> | UserSettingsDoc | undefined
  group: Model<GroupDoc> | GroupDoc
  LastEvent?: Model<EventDoc>
  default: boolean
  muted: boolean
  hidden: boolean
  pinned: boolean
  onClick?: () => void
  unread?: number
  handleContextMenu: (
    event: React.MouseEvent<HTMLElement>,
    group: GroupDoc
  ) => void
  disableSelection?: boolean
  hideMetaData?: boolean
  lastUpdateMessagePerGroup?: LastUpdateMessagePerGroup
}

function GroupCard(props: GroupCardProps) {
  const theme = useTheme()
  const calendar = useCalendar()
  const { group, LastEvent } = props

  const groupsSettings = props.userSettings?.GroupsSettings
  const groupColor = groupsSettings?.[props.group?._id ?? '']?.GroupColor
  const isSyncProblem = group.SyncProblem === '1'
  const isBlocked =
    isGroupOutOfTier(highestActiveTier(props.account), group, props.account) ||
    isSyncProblem
  const lockIcon = isSyncProblem ? <GoogleProblem /> : null

  const lockAction = () => {
    if (isSyncProblem) {
      onShowSyncProblemDialog(true)
    }
  }

  const hideSyncProblem = () => {
    onShowSyncProblemDialog(false)
  }

  const { t, i18n } = useTranslation()

  const [showSyncProblemDialog, onShowSyncProblemDialog] = useState(false)

  function onGrantedCalendarAccess(calendar: ICalendar) {
    Database.write(async () => {
      try {
        if ((props.group._rev?.length ?? 0) > 0)
          if (props.group.id)
            await props.group.update((local: GroupDoc) => {
              local.SyncProblem = null
            })
      } catch (e) {
        console.error(e)
      }
    })
  }

  function initiateGoogleSync() {
    calendar.initiateGoogleSync(
      (cal) => {
        onGrantedCalendarAccess(cal)
        calendar.onGroupServer(`${prefixGoogleEventsGroup}${cal.id}`)
        onShowProgress(false)
      },
      () => {
        onShowProgress(false)
      },
      () => {
        onShowProgress(true)
      }
    )
    hideSyncProblem()
  }

  const [showProgress, onShowProgress] = useState(false)

  let subLabel =
    (isConvertedGroup(group) || group._id?.includes('tempid')) &&
    isTrulyEmpty(group.PrivateLinkUrl) &&
    isTrulyEmpty(group.PrivateLinkPassword)
      ? t('private')
      : isGroupArchived(props.userSettings, group._id ?? '')
        ? t('archived')
        : undefined

  const lastMessage =
    props.lastUpdateMessagePerGroup?.lastUpdateMessage ??
    getGroupMessage(
      group,
      (props.events ?? [])
        .filter(
          (event) =>
            event.GroupID === props.group._id ||
            event.SupplementaryGroupsIDs?.includes(props.group._id ?? '')
        )
        .sort((a, b) => Number(b.LastUpdate) - Number(a.LastUpdate))
        .find((event) => event.LastUpdateMessage)
    )
  const iconSize = 16

  const onContextMenu = (event: React.MouseEvent<HTMLElement>) => {
    props.handleContextMenu(event, props.group)
    LocalStorage.set(LocalStorageKey.CONTEXT_FOR_GROUP, props.group._id)
  }

  const showOnlyMyEvents =
    groupsSettings?.[props.group._id ?? '']?.ShoWOnlyEventsIOwn === '1'

  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (group._id === calendar.groupServerID && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [calendar.groupServerID, group._id])

  return (
    <Styled.GroupCardItem
      key={isTrulyEmpty(props.group._id) ? undefined : props.group._id}
      id={`${
        isConvertedGroup(props.group)
          ? removePrefix(props.group?.CalendarEmail ?? '')
          : props.group?.Name
      }${(group._id?.includes(tfmGoogleTempLabel)
        ? APP_COLOR
        : (groupColor ?? group.GroupColor ?? APP_COLOR)
      ).replace('#', '')}`}
      ref={cardRef}
      overflow={'hidden'}
      data-rev={props.group._rev}
      style={{
        border: `1px solid ${
          group._id === LocalStorage.get(LocalStorageKey.SELECTED_GROUP) ||
          group._id === LocalStorage.get(LocalStorageKey.CONTEXT_FOR_GROUP)
            ? theme.palette.border.primary
            : 'color-mix(in srgb, var(--color-border-default) 25%, transparent)'
        }`
      }}
    >
      <Styled.GroupCardButton
        sx={{
          alignItems: 'start',
          backgroundColor: isBlocked
            ? theme.palette.surface.active
            : theme.palette.card.primary,
          padding: theme.spacing(2),
          '&.Mui-selected': {
            backgroundColor: `${
              theme.palette.mode === 'dark'
                ? alpha(APP_COLOR, 0.4)
                : lighten(APP_COLOR, 0.8)
            } !important`
          }
        }}
        onClick={props.onClick}
        className="groupItem"
        onContextMenu={onContextMenu}
        selected={
          props.disableSelection ? false : group._id === calendar.groupServerID
        }
      >
        {showProgress ? (
          <CircularProgress color="inherit" />
        ) : (
          <ContainerWithBottomBadgeIcon
            showLock={isBlocked || isSyncProblem}
            lockIcon={lockIcon}
            topRightIcon={
              isGroupChannel(props.group) ? (
                <GroupChannelIcon width={18} height={18} />
              ) : undefined
            }
            action={lockAction}
          >
            <Styled.GroupCardAvatarContainer
              color={
                group._id?.includes(tfmGoogleTempLabel)
                  ? APP_COLOR
                  : (groupColor ?? group.GroupColor)
              }
            >
              <Styled.GroupCardAvatar
                asGroup
                img={group.Photo}
                name=""
                emptyPic={
                  group._id === 'tempid'
                    ? calendarsGif
                    : defaultGroupImage(group)
                }
              />
            </Styled.GroupCardAvatarContainer>
          </ContainerWithBottomBadgeIcon>
        )}
        <Box
          display={'flex'}
          flexDirection={'column'}
          width={'100%'}
          marginLeft={2}
        >
          <Styled.GroupCardTitleContainer>
            <div
              style={{
                maxWidth: '180px',
                whiteSpace: 'pre-line',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {
                <GroupTitle
                  weight={500}
                  locked={isBlocked}
                  group={group}
                  iconHeight={'16px'}
                />
              }
            </div>
            {subLabel ? (
              <PrivateLabel>{subLabel}</PrivateLabel>
            ) : (
              <Styled.GroupCardTime
                id={`${group.Name}${group.GroupColor?.replace('#', '')}-last-update`}
              >
                {formatLastUpdate(
                  group._sortOrder && group._sortOrder > 0
                    ? group._sortOrder
                    : Number(group.LastUpdate)
                )}
              </Styled.GroupCardTime>
            )}
          </Styled.GroupCardTitleContainer>
          <Grid2 container display={'flex'} flexDirection={'row'}>
            <Grid
              container
              item
              flex={1}
              display={'flex'}
              flexDirection={'column'}
              marginTop={2}
            >
              <Box display={'flex'}>
                {lastMessage?.type && (
                  <TitleWithIconRow
                    id={`${props.group.Name}${props.group.GroupColor?.replace('#', '')}-message-type`}
                    icon={
                      <MsgType
                        style={{
                          minWidth: iconSize
                        }}
                        width={iconSize}
                        height={iconSize}
                      />
                    }
                    title={lastMessage.type}
                  />
                )}
              </Box>
              <Box display={'flex'} flex={1}>
                {lastMessage?.title && (
                  <TitleWithIconRow
                    icon={
                      <MsgTitle
                        style={{ minWidth: iconSize, minHeight: iconSize }}
                      />
                    }
                    title={lastMessage.title}
                  />
                )}
              </Box>

              {lastMessage?.when && (
                <TitleWithIconRow
                  icon={<MsgWhen width={iconSize} height={iconSize} />}
                  title={lastMessage.when}
                />
              )}
              {lastMessage?.who && (
                <TitleWithIconRow
                  icon={<MsgWho width={iconSize} height={iconSize} />}
                  title={lastMessage.who}
                />
              )}
            </Grid>
            <Grid2
              container
              display={'flex'}
              flexDirection={'column'}
              justifyContent={'center'}
              alignItems={'center'}
              gap={
                isGroupUnread(props.userSettings, props.group._id ?? '') ||
                (props?.unread ?? 0)
                  ? '0.2rem'
                  : undefined
              }
              marginTop={'0.7rem'}
            >
              <Styled.GroupIcon>
                {isGroupUnread(props.userSettings, props.group._id ?? '') ? (
                  <Styled.UnreadAmount></Styled.UnreadAmount>
                ) : (
                  (props?.unread ?? 0) > 0 && (
                    <Styled.UnreadAmount count={props.unread}>
                      {props.unread}
                    </Styled.UnreadAmount>
                  )
                )}
              </Styled.GroupIcon>
              <Box
                gap={0.75}
                display={'flex'}
                flexDirection={'row'}
                justifyContent={'center'}
                alignItems={'center'}
              >
                {props.default && (
                  <GroupcalTooltip title={t('defaultCalendar')}>
                    <GroupMenuDefault />
                  </GroupcalTooltip>
                )}
                {props.muted && (
                  <GroupcalTooltip title={t('disableNotifications')}>
                    <GroupMenuReminders />
                  </GroupcalTooltip>
                )}
                {props.hidden && (
                  <GroupcalTooltip
                    title={t('dontShowOnAllCal', {
                      cal: t('allCalendarsButton')
                    })}
                  >
                    <GroupMenuAllCalendars id="hidden-all-calendars" />
                  </GroupcalTooltip>
                )}
                {props.pinned && (
                  <GroupcalTooltip title={t('pinGroup')}>
                    <PinIcon />
                  </GroupcalTooltip>
                )}
                {showOnlyMyEvents && (
                  <GroupcalTooltip title={t('showOnlyYourEvents')}>
                    <GroupMenuYouOwn />
                  </GroupcalTooltip>
                )}
              </Box>
            </Grid2>
          </Grid2>
        </Box>
      </Styled.GroupCardButton>
      {showSyncProblemDialog && (
        <ResyncCalendarDialog
          isOpen={showSyncProblemDialog}
          setOpen={onShowSyncProblemDialog}
          group={props.group}
        />
      )}
    </Styled.GroupCardItem>
  )
}

export default withObservables(
  ['group', 'events'],
  (props: GroupCardProps) => ({
    userSettings: ObserveService.UserSettings(),
    events: ObserveService.Events(undefined, []),
    account: ObserveService.Account(),
    LastEvent: ObserveService.GroupLastEvent(props.group)
  })
)(GroupCard)
